import { useCallback } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isNumber from "lodash-es/isNumber"

import { captureCoachingChatSocketExceptionWithSentry, reduxBatch, safeTransition } from "@/3514/utils"
import { type TEmptyCallback } from "@/shared/types/functions"

import {
    useCoachingChatSocketReceiver as useReceiver,
    useCoachingChatSocketReceiver as useSocketReceiver
} from "../api"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EApiMode,
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatSocketErrorReason as ESocketErrorReason,
    ECoachingChatSocketListenerEventName as ESocketEventName,
    ECoachingChatSocketListenerEventName as ESocketListenerEventName,
    type TCoachingChatSocketErrorEventResponse as TSocketErrorEventResponse,
    type _TCoachingChatHookSharedParams as _TParams
} from "../types"

import {
    type TCoachingChatRestartConversationFunction as TRestartConversationFunction,
    useCoachingChatConversationRestarter as useConversationRestarter
} from "./coaching-chat.conversation-restart.hooks"

type _TGeneralListenerProps = _TParams
function useGeneralListener({ entityId, entityMode, wasChatCompleted }: _TGeneralListenerProps): void {
    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const conversationId: number | null = useConversationIdSelector(entityId)

    const restartConversation: TRestartConversationFunction = useConversationRestarter({ entityId })

    const isSocketsMode: boolean = eq(conversationApiMode, EConversationApiMode.Sockets)

    const isEnabled: boolean = isSocketsMode && eq(entityMode, EEntityMode.Full) && !wasChatCompleted

    const handler: (resp: TSocketErrorEventResponse) => void = useCallback(
        (resp: TSocketErrorEventResponse): void => {
            reduxBatch((): void => {
                switch (resp.code) {
                    case ESocketErrorReason.JsonDecodeError:
                    case ESocketErrorReason.SchemaValidationError:
                    case ESocketErrorReason.ValidationError:
                    case ESocketErrorReason.GeneralError:
                    case ESocketErrorReason.StreamingIdle:
                    case ESocketErrorReason.StreamingFatalIdle:
                    case ESocketErrorReason.FirstChunkMissing:
                    case ESocketErrorReason.Connection:
                    case ESocketErrorReason.RateLimit:
                        conversationDispatcher.changeStatus(EConversationStatus.Paused)
                        conversationDispatcher.triggerError(EConversationErrorVariant.StreamingFailed)
                        break

                    case ESocketErrorReason.SummaryRequired:
                    case ESocketErrorReason.ParticipantNotFound:
                    case ESocketErrorReason.InvalidPayload:
                    case ESocketErrorReason.ConversationExists:
                        conversationDispatcher.changeStatus(EConversationStatus.Paused)
                        conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
                        conversationDispatcher.triggerError(EConversationErrorVariant.ApiFailure)
                        break

                    case ESocketErrorReason.ConversationNotFound:
                        conversationDispatcher.changeStatus(EConversationStatus.Paused)
                        conversationDispatcher.removeMessagesRight(1)
                        conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
                        conversationDispatcher.triggerError(EConversationErrorVariant.ApiFailure)
                        break

                    case ESocketErrorReason.Guardrail:
                    case ESocketErrorReason.GuardrailInternalError:
                        conversationDispatcher.changeStatus(EConversationStatus.InProgress)
                        conversationDispatcher.triggerError(EConversationErrorVariant.Violation, {
                            message: resp.message,
                            messageId: resp.moderation.message_id,
                            moderationId: resp.moderation.moderation_id
                        })
                        conversationDispatcher.markInput({ isDimmed: false, isDisabled: false })
                        conversationDispatcher.removeMessagesRight(2)
                        break

                    case ESocketErrorReason.TemplateCorrupted:
                    case ESocketErrorReason.ConversationFailed:
                        conversationDispatcher.changeStatus(EConversationStatus.Paused)
                        conversationDispatcher.triggerError(EConversationErrorVariant.AiStuck)
                        conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
                        conversationDispatcher.removeMessagesRight(1)
                        break

                    case ESocketErrorReason.RestartNotAllowed:
                        conversationDispatcher.changeStatus(EConversationStatus.Paused)
                        conversationDispatcher.triggerError(EConversationErrorVariant.AiStuck)
                        conversationDispatcher.markInput({ isDimmed: true, isDisabled: true })
                        break

                    case ESocketErrorReason.AlreadyInProgress:
                        conversationDispatcher.changeStatus(EConversationStatus.Paused)
                        conversationDispatcher.clearError()
                        break

                    default:
                        break
                }
            })

            if (resp.retryable && eq(resp.suggested_action, "cm_restart") && resp.phase !== "streaming") {
                if (isNumber(resp.participant_message_id)) void restartConversation(resp.participant_message_id)
                else void restartConversation()
            }

            switch (resp.code) {
                case ESocketErrorReason.JsonDecodeError:
                case ESocketErrorReason.SchemaValidationError:
                case ESocketErrorReason.ValidationError:
                case ESocketErrorReason.GeneralError:
                case ESocketErrorReason.StreamingIdle:
                case ESocketErrorReason.StreamingFatalIdle:
                case ESocketErrorReason.FirstChunkMissing:
                case ESocketErrorReason.Connection:
                case ESocketErrorReason.SummaryRequired:
                case ESocketErrorReason.RateLimit:
                case ESocketErrorReason.InvalidPayload:
                case ESocketErrorReason.ConversationNotFound:
                case ESocketErrorReason.GuardrailInternalError:
                case ESocketErrorReason.TemplateCorrupted:
                case ESocketErrorReason.ConversationFailed:
                    captureCoachingChatSocketExceptionWithSentry(resp, conversationId)
                    break
                default:
                    break
            }
        },
        [restartConversation, conversationDispatcher, conversationId]
    )

    useSocketReceiver<TSocketErrorEventResponse>({
        isEnabled,
        event: ESocketEventName.Error,
        handler
    })
}

type _TStreamerListenerProps = Pick<_TParams, "entityId"> & {
    handleError(resp: TSocketErrorEventResponse): Promise<void>
}
function useStreamerListener({ entityId, handleError }: _TStreamerListenerProps): void {
    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const isEnabled: boolean =
        eq(conversationApiMode, EApiMode.Sockets) &&
        includes<EConversationStatus>([EConversationStatus.Created, EConversationStatus.InProgress], conversationStatus)

    useSocketReceiver<TSocketErrorEventResponse>({
        isEnabled,
        event: ESocketEventName.Error,
        handler: (resp: TSocketErrorEventResponse): void => {
            switch (resp.code) {
                case ESocketErrorReason.GeneralError:
                case ESocketErrorReason.JsonDecodeError:
                case ESocketErrorReason.SummaryRequired:
                case ESocketErrorReason.Guardrail:
                case ESocketErrorReason.GuardrailInternalError:
                case ESocketErrorReason.StreamingIdle:
                case ESocketErrorReason.StreamingFatalIdle:
                case ESocketErrorReason.FirstChunkMissing:
                case ESocketErrorReason.SchemaValidationError:
                case ESocketErrorReason.ValidationError:
                    safeTransition(async (): Promise<void> => (await handleError(resp), void 0))
                    break
                default:
                    break
            }
        }
    })
}

type _TConversationStarterListenerProps = {
    handleError: TEmptyCallback
}
function useConversationStarterListener({ handleError }: _TConversationStarterListenerProps): void {
    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const isEnabled: boolean = eq(conversationApiMode, EApiMode.Sockets)

    useReceiver<TSocketErrorEventResponse>({
        isEnabled,
        event: ESocketListenerEventName.Error,
        handler: (resp: TSocketErrorEventResponse): void => {
            if (eq(resp.code, ESocketErrorReason.DisclaimerRequired)) safeTransition((): void => handleError())
        }
    })
}

export {
    useGeneralListener as useCoachingChatSocketErrorGeneralListener,
    useStreamerListener as useCoachingChatSocketErrorStreamerListener,
    useConversationStarterListener as useCoachingChatSocketErrorConversationStarterListener
}
